import React, { ReactNode, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import GetHeroIcon from "../../utils/getHeroIcon";
import "./scrollbar.css";

export interface Tabprop {
  label: string;
  iconposition?: "leading" | "trailing";
  iconname?: string;
  disabled?: boolean; // Add disabled property
  testId?: string;
  route?: string;
  hasError?: boolean;
  pill?: number | string | undefined;
}

interface Props {
  tabs: Tabprop[];
  children: ReactNode[];
  defaultValue?: number;
  handleState?: (data: number) => void;
  style?: "pills" | "underline" | "bgPills";
  enableNavigation?: boolean;
  enableTabParamMutations?: boolean;
  smText?: boolean;
  navigateTo?: number;
}

const Tabs: React.FC<Props> = ({
  tabs,
  style,
  defaultValue = 0,
  children,
  handleState,
  navigateTo,
  smText = false,
  enableTabParamMutations = true
}) => {
  const [activeTab, setActiveTab] = useState<number>(defaultValue || 0);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "0";

  useEffect(() => {
    if (
      tabs.length > 1 &&
      !isNaN(Number(tabParam)) &&
      Number(tabParam) !== activeTab &&
      enableTabParamMutations
    ) {
      setActiveTab(Number(tabParam));
    }
  }, [tabParam, tabs.length]);

  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== activeTab) {
      setActiveTab(defaultValue);
    }
  }, [defaultValue]);

  const handleClick = (index: number) => {
    setActiveTab(index);
    if (handleState) {
      handleState(index);
    }
    if (enableTabParamMutations) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", index.toString());
      setSearchParams(newParams);
    }
  };

  useEffect(() => {
    if (
      typeof navigateTo != "undefined" &&
      navigateTo != null &&
      !isNaN(Number(navigateTo))
      // Number(navigateTo) !== activeTab
    ) {
      onNavigationChange(navigateTo);
    }
  }, [navigateTo]);

  const onNavigationChange = (index: number) => {
    if (enableTabParamMutations) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", index.toString());
      setSearchParams(newParams);
    }
    setActiveTab(index);
  };
  return (
    <div>
      <div className={`${style === "underline" ? "" : "block"} `}>
        <nav
          className={`${
            style === "underline"
              ? "-mb-px flex border-b-2 dark:border-border"
              : style === "bgPills"
                ? " bg-secondary/5 flex space-x-4 "
                : "flex space-x-4 "
          } overflow-x-auto tab`}
        >
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => !tab.disabled && handleClick(index)}
              className={` flex justify-center
                ${smText ? "text-sm" : "text-base"}
                focus-visible:!outline-none focus-visible:!ring-2 focus-visible:!ring-inset focus-visible:!ring-black
              ${
                style === "underline"
                  ? " w-full sm:min-w-1/4 border-b-2 py-4 px-1 text-center font-medium"
                  : style === "bgPills"
                    ? "my-2 mx-2 rounded-md px-3 py-2 font-medium"
                    : "rounded-md px-3 py-2 font-medium"
              }
              ${
                style === "underline" && activeTab === index
                  ? "border-primary text-primary"
                  : style === "underline" && activeTab !== index
                    ? "border-transparent text-secondary hover:border-secondary/70 hover:text-secondary/70"
                    : style !== "underline" && activeTab === index
                      ? "bg-primary-light dark:bg-zinc-700 dark:text-secondary text-primary"
                      : "text-secondary hover:text-secondary/70"
              }
              ${tab.iconname ? "mx-3" : "group inline-flex items-center"}
              ${tab.disabled ? "opacity-50 cursor-not-allowed" : ""} `}
              data-testid={tab.testId}
            >
              {tab.hasError ? (
                <>
                  <span className="text-red-500"> {tab.label}</span>
                  <GetHeroIcon
                    name="ExclamationCircleIcon"
                    className="w-5 h-5 ml-1 text-red-500"
                    variant="outline"
                  />
                </>
              ) : tab.iconname && tab.iconposition === undefined ? (
                <>
                  <GetHeroIcon
                    name={tab.iconname}
                    className="w-5 h-5 mr-2"
                    aria-hidden="true"
                  />
                  <span className="hidden sm:inline">{tab.label}</span>
                </>
              ) : tab.iconname && tab.iconposition === "leading" ? (
                <>
                  <GetHeroIcon
                    name={tab.iconname}
                    className="w-5 h-5 mr-2"
                    aria-hidden="true"
                  />
                  {tab.label}
                </>
              ) : tab.iconname && tab.iconposition === "trailing" ? (
                <>
                  {tab.label}
                  <GetHeroIcon
                    name={tab.iconname}
                    className="w-5 h-5 ml-1"
                    aria-hidden="true"
                  />
                </>
              ) : (
                <>
                  {tab.label}
                  {tab.pill === undefined ||
                  tab.pill === null ||
                  tab.pill === "" ? null : (
                    <span className="ml-2 rounded-full py-1 px-3 text-white dark:text-gray-100 font-semibold bg-gray-600 dark:bg-gray-800">
                      {tab.pill}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>
        {style !== "underline" ? (
          <hr className="h-px my-3 border-0 bg-secondary/20 dark:bg-border" />
        ) : (
          <hr className="h-0 my-3 border-0 bg-secondary/20 dark:bg-border" />
        )}
        <div
          className={`relative w-full h-full overflow-auto opacity-0 ease-in-out ${
            activeTab !== undefined ? "opacity-100" : "opacity-0"
          }	`}
        >
          {children[activeTab]}
        </div>
      </div>
    </div>
  );
};

export default Tabs;
